// Free of locations-data.json so client components can import it. The only data
// it reads is the small enrichment file, and only once the gate below is on.
import type { LocationData, PageType } from '@/types/locations';
import { isEnriched } from '@/lib/locationEnrichment';

/**
 * Issue #459: index only places whose numbers are genuinely local. When on, a
 * place's indexed page types are every non-consolidated type if it is fully
 * enriched (src/data/location-enrichment.json), and none if it is not. This
 * replaces the hand-picked FULL_SET_TOWNS list with a rule.
 *
 * Off until the phase-one data is in: switching it on with an empty data file
 * would noindex every location page at once. Turn it on in the same change that
 * adds the phase-one places, then request indexing for them in GSC.
 */
export const ENRICHMENT_GATE_ACTIVE = false;

export const LOCATION_PAGE_TYPES: PageType[] = [
  'how-much-do-i-need-to-retire',
  'financial-advisors',
  'wealth-managers',
  'financial-planners',
  'financial-coaching',
  'retirement-planning',
  'financial-guidance',
  'financial-mentoring',
  'financial-therapists',
];

/**
 * Towns only carry the two page types with real search demand at town level
 * (GSC, Jul–Sep 2026). The other seven town pages still render for anyone who
 * lands on them, but are noindexed, left out of the sitemap and not linked
 * internally, so 3,240 near-identical URLs don't read as a thin-content footprint.
 */
export const TOWN_PAGE_TYPES: PageType[] = ['how-much-do-i-need-to-retire', 'financial-advisors'];

/**
 * The four page types that ask a commercial question the place's
 * financial-advisors page already answers. Position slide, Aug–Oct 2026: they
 * rank 39–61, earn 0–2 clicks a week between them, and four intents for one
 * place is a thin-content footprint. Towns already noindexed them; cities and
 * counties did not, and the city commercial pages are the ones that slid
 * (financial advice Leeds 71, Cambridge 54, Mid Devon 62).
 *
 * Consolidated for every place type, so the two treatment paths are one. These
 * pages still render for anyone who lands on them, but they are noindex follow,
 * canonical to the place's financial-advisors page, out of the sitemap and out
 * of the page-type navigation. The five that stay indexed have real and distinct
 * demand: how-much-do-i-need-to-retire (position 8), financial-advisors
 * (2,098 impressions), retirement-planning (~500), financial-therapists (9–15),
 * financial-mentoring (5–9).
 *
 * Assessed against the DEL-616 archetype rewrite, which went live 19 Oct 2026
 * — three weeks after the slide began, so it is not the cause and its effect
 * was not yet visible when these numbers were taken.
 */
export const CONSOLIDATED_PAGE_TYPES: PageType[] = [
  'wealth-managers',
  'financial-planners',
  'financial-coaching',
  'financial-guidance',
];

/**
 * Towns that keep every page type because their other pages had real demand
 * (500+ impressions in 90 days, GSC Jul–Sep 2026). Exeter and Norwich are
 * cities that the local-authority dataset types as towns.
 */
const FULL_SET_TOWNS = new Set([
  'exeter',
  'stockton-on-tees',
  'telford-and-wrekin',
  'blackpool',
  'watford',
  'burnley',
  'norwich',
  'denbighshire',
  'east-ayrshire',
]);

type TierFields = Pick<LocationData, 'type' | 'slug'>;

export interface TierOptions {
  /** Defaults to ENRICHMENT_GATE_ACTIVE; tests pass it explicitly. */
  gate?: boolean;
  /** Defaults to the enrichment data file; tests inject their own. */
  enriched?: (location: TierFields) => boolean;
}

export function isIndexedLocationPage(location: TierFields, pageType: PageType, options: TierOptions = {}): boolean {
  if (CONSOLIDATED_PAGE_TYPES.includes(pageType)) {
    return false;
  }
  const { gate = ENRICHMENT_GATE_ACTIVE, enriched = isEnriched } = options;
  if (gate) {
    return enriched(location);
  }
  return location.type !== 'town' || FULL_SET_TOWNS.has(location.slug) || TOWN_PAGE_TYPES.includes(pageType);
}

export function indexedPageTypesFor(location: TierFields, options: TierOptions = {}): PageType[] {
  return LOCATION_PAGE_TYPES.filter(pt => isIndexedLocationPage(location, pt, options));
}
